import { useEffect, useCallback, useMemo } from 'react'
import {
  useInitData,
  useLaunchParams,
  useThemeParams,
  useViewport,
  useMiniApp,
  useHapticFeedback,
  useMainButton,
  useBackButton,
  usePopup,
  useClosingBehavior,
  useSwipeBehavior,
  useUtils,
} from '@telegram-apps/sdk-react'
import { TelegramCtx, type TelegramContextType } from './useTelegram'

export function TelegramSDKBridge({ children }: { children: React.ReactNode }) {
  const initData = useInitData()
  // ssr=true returns `undefined` instead of throwing when launch params
  // are not retrievable (e.g. after a Telegram "Обновить страницу" drops
  // the URL hash). Downstream consumers already optional-chain on the value.
  const launchParams = useLaunchParams(true)
  const themeParams = useThemeParams()
  const viewport = useViewport()
  const miniApp = useMiniApp()
  const haptic = useHapticFeedback()
  const mainButton = useMainButton()
  const backButton = useBackButton()
  const popup = usePopup()
  const closingBehavior = useClosingBehavior()
  const swipeBehavior = useSwipeBehavior(true)
  const utils = useUtils()

  // Expand viewport on mount
  useEffect(() => {
    if (viewport && !viewport.isExpanded) {
      viewport.expand()
    }
  }, [viewport])

  // Вертикальный свайп в Telegram сворачивает мини-апп. На длинных экранах
  // (дневник, список упражнений, аналитика) пользователь докручивает страницу
  // вверх и по инерции тянет дальше — приложение схлопывается посреди
  // тренировки. Отключаем именно ЖЕСТ сворачивания: прокрутка контента живёт
  // в WebView и этой командой не затрагивается.
  //
  // Bot API 7.7+; на старых клиентах хук вернёт undefined и всё останется
  // как раньше. Закрыть приложение по-прежнему можно крестиком, свайпом по
  // шапке и кнопкой «назад».
  useEffect(() => {
    if (!swipeBehavior) return
    if (swipeBehavior.isVerticalSwipeEnabled) {
      swipeBehavior.disableVerticalSwipe()
    }
  }, [swipeBehavior])

  // Enable closing confirmation
  useEffect(() => {
    if (closingBehavior) {
      closingBehavior.enableConfirmation()
    }
    return () => {
      if (closingBehavior) {
        closingBehavior.disableConfirmation()
      }
    }
  }, [closingBehavior])

  const getInitDataRaw = useCallback(() => {
    // Primary source is the SDK launch params, but Telegram drops the URL hash
    // on reopen / "Обновить страницу", leaving launchParams without initDataRaw.
    // telegram-web-app.js keeps WebApp.initData populated via the native bridge
    // across reopens, so fall back to it — otherwise auth silently no-ops and
    // the app shows «Не удалось авторизоваться».
    return launchParams?.initDataRaw || window.Telegram?.WebApp?.initData || ''
  }, [launchParams])

  const user = initData?.user

  const isDark = themeParams?.isDark ?? false
  const bgColor = themeParams?.bgColor || '#ffffff'
  const textColor = themeParams?.textColor || '#000000'
  const buttonColor = themeParams?.buttonColor || '#3390ec'
  const buttonTextColor = themeParams?.buttonTextColor || '#ffffff'
  const secondaryBgColor = themeParams?.secondaryBgColor || '#f0f0f0'
  const hintColor = themeParams?.hintColor || '#999999'
  const linkColor = themeParams?.linkColor || '#3390ec'

  const impactHaptic = useCallback(
    (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
      haptic?.impactOccurred(style)
    },
    [haptic]
  )

  const notificationHaptic = useCallback(
    (type: 'error' | 'success' | 'warning' = 'success') => {
      haptic?.notificationOccurred(type)
    },
    [haptic]
  )

  const selectionHaptic = useCallback(() => {
    haptic?.selectionChanged()
  }, [haptic])

  const showMainButton = useCallback(
    (text: string, onClick: () => void) => {
      if (mainButton) {
        mainButton.setText(text)
        mainButton.on('click', onClick)
        mainButton.show()
      }
    },
    [mainButton]
  )

  const hideMainButton = useCallback(() => {
    mainButton?.hide()
  }, [mainButton])

  const setMainButtonLoading = useCallback(
    (loading: boolean) => {
      if (loading) {
        mainButton?.showLoader()
      } else {
        mainButton?.hideLoader()
      }
    },
    [mainButton]
  )

  const showBackButton = useCallback(
    (onClick: () => void) => {
      if (backButton) {
        backButton.on('click', onClick)
        backButton.show()
      }
    },
    [backButton]
  )

  const hideBackButton = useCallback(() => {
    backButton?.hide()
  }, [backButton])

  const showAlert = useCallback(
    (message: string) => {
      popup?.open({ message })
    },
    [popup]
  )

  const showConfirm = useCallback(
    async (message: string): Promise<boolean> => {
      if (!popup) return false
      const result = await popup.open({
        message,
        buttons: [
          { id: 'cancel', type: 'cancel' },
          { id: 'ok', type: 'ok' },
        ],
      })
      return result === 'ok'
    },
    [popup]
  )

  const openLink = useCallback(
    (url: string, opts?: { external?: boolean }) => {
      if (utils) {
        // #419: чужой логин (FatSecret) во встроенном браузере Telegram —
        // тупик. Адресной строки нет, ссылку не скопировать, а письмо со
        // сбросом пароля открывается в системном браузере — другая сессия,
        // и FatSecret отвечает «ссылка недействительна». В системном браузере
        // весь путь проходит в одном месте. Возврат не ломается: страница
        // успеха уводит обратно по ссылке на бота.
        utils.openLink(url, opts?.external ? { tryBrowser: true } : undefined)
      } else {
        window.open(url, '_blank')
      }
    },
    [utils]
  )

  // Открывает t.me-ссылку (чат с ботом) внутри Telegram и закрывает Mini App.
  // Фоллбэк на openLink/window.open вне Telegram (dev/браузер).
  const openTelegramLink = useCallback(
    (url: string) => {
      const u = utils as unknown as { openTelegramLink?: (url: string) => void } | null
      if (u?.openTelegramLink) {
        u.openTelegramLink(url)
      } else if (utils) {
        utils.openLink(url)
      } else {
        window.open(url, '_blank')
      }
    },
    [utils]
  )

  const ready = useCallback(() => {
    miniApp?.ready()
  }, [miniApp])

  const close = useCallback(() => {
    miniApp?.close()
  }, [miniApp])

  const value: TelegramContextType = useMemo(
    () => ({
      initData,
      getInitDataRaw,
      user,
      launchParams,
      themeParams,
      isDark,
      bgColor,
      textColor,
      buttonColor,
      buttonTextColor,
      secondaryBgColor,
      hintColor,
      linkColor,
      viewport,
      viewportHeight: viewport?.height || window.innerHeight,
      viewportStableHeight: viewport?.stableHeight || window.innerHeight,
      isExpanded: viewport?.isExpanded ?? true,
      impactHaptic,
      notificationHaptic,
      selectionHaptic,
      showMainButton,
      hideMainButton,
      setMainButtonLoading,
      mainButton,
      showBackButton,
      hideBackButton,
      backButton,
      showAlert,
      showConfirm,
      popup,
      openLink,
      openTelegramLink,
      ready,
      close,
      miniApp,
    }),
    [
      initData, getInitDataRaw, user, launchParams, themeParams,
      isDark, bgColor, textColor, buttonColor, buttonTextColor,
      secondaryBgColor, hintColor, linkColor, viewport,
      impactHaptic, notificationHaptic, selectionHaptic,
      showMainButton, hideMainButton, setMainButtonLoading, mainButton,
      showBackButton, hideBackButton, backButton,
      showAlert, showConfirm, popup, openLink, openTelegramLink, ready, close, miniApp,
    ]
  )

  return <TelegramCtx.Provider value={value}>{children}</TelegramCtx.Provider>
}
